import { useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from 'react-intl';

//Components
import { Typography } from '@mui/material';
import { Modal, ModalBody, Button } from "@gpstrackit/react-controls";
import { NotificationManager } from "Components/Notifications";
import { forTesting } from 'Core/data/Helpers';

//Views
import CreateEditView from 'Modules/dashboard/views/.components/dashboardPanel/.components/gadgets/.components/createEditView';

//Styles
import './index.scss';

//Actions
import {
    createGadgetDashboardPanel,
    sizeDashboardPanelCreateEditModal,
    openDashboardPanelCreateEditModal,
    updateGadgetDashboardPanel,
    clearRedux,
} from 'Redux/actions';

//Permission
import { HasPermission } from "Core/security";

const CreateEditGadget = (props) => {
    const { dashboardId, callback } = props;

    const { messages } = useIntl();
    const dispatch = useDispatch();

    const user = useSelector(state => state.securityRedux.user);

    //Options
    const gadGetsType = [
        {
            "name": messages['create_edit_gadget_chart'],
            "description": messages['create_edit_gadget_chart_description'],
            "type": "Chart",
            "visible": HasPermission(user, "gtchrts", "c")
        },
        {
            "name": messages['create_edit_gadget_pie_title'],
            "description": messages['create_edit_gadget_pie_description'],
            "type": "ClipRequestByStatus",
            "visible": HasPermission(user, "gtvfcr", "c")
        },
        {
            "name": "VidFleet Firmwares",
            "description": messages['create_edit_gadget_firmware_description'],
            "type": "VidFleetFirmware",
            "visible": HasPermission(user, "gtvffw", "c")
        },
        {
            "name": messages['create_edit_gadget_unit_by_status'],
            "description": "",
            "type": "DevicesByStatus",
            "visible": true
        },
        {
            "name": messages['create_edit_gadget_stopped_reporting'],
            "description": "",
            "type": "UnitsThatStoppedReporting",
            "visible": true
        },
        {
            "name": messages['create_edit_gadget_maintenance_warnings'],
            "description": "",
            "type": "MaintenanceWarnings",
            "visible": true
        }
    ];

    //Local States
    const [selectGadget, setSelectGadget] = useState({});
    const urlImagePreview = `/assets/images/`;

    //Redux States
    const {
        loadingCreateEdit,
        openCreateEdit,
        size_modal,
        gadGet,
        updateResponse,
        createUserGadget,
        errorUserGadget
    } = useSelector((state) => state.dashboardPanelMainRedux);

    const handleClose = useCallback(() => {
        setSelectGadget({})
        dispatch(sizeDashboardPanelCreateEditModal('small'));
        dispatch(openDashboardPanelCreateEditModal(false));
        dispatch(clearRedux("DASHBOARD_PANEL_MAIN", {
            openCreateEdit: false,
            loadingCreateEdit: false,
            updateResponse: false,
            errorUserGadget: null,
            gadGet: {}
        }));
    });

    const returnMain = useCallback(() => {
        if (gadGet.hasOwnProperty('name')) {
            handleClose();
        } else {
            setSelectGadget({})
        }
    });

    const onSubmit = (data) => {
        let settings = JSON.stringify(data);
        let values;
        if (gadGet.hasOwnProperty('name')) {
            values = {
                ...gadGet,
                name: data.name,
                dashboardId: dashboardId,
                settings,
                refreshSeconds: parseInt(data.refreshSeconds)
            };
            dispatch(updateGadgetDashboardPanel(values));
        } else {
            values = {
                ...selectGadget,
                name: data.name,
                dashboardId: dashboardId,
                settings,
                refreshSeconds: parseInt(data.refreshSeconds),
                columnPanel: 1,
                columnOrder: 0
            };
            dispatch(createGadgetDashboardPanel(values));
        }
    };

    useEffect(() => {
        if (createUserGadget?.hasOwnProperty('name')) {
            handleClose();
            NotificationManager.success(
                messages['create_edit_gadget_changes_saved_sucessfully'],
                messages['create_edit_gadget_create_gadget'],
                4000
            );
        }
    }, [createUserGadget])

    useEffect(() => {
        if (updateResponse) {
            callback(updateResponse);
            handleClose();
            NotificationManager.success(
                messages['create_edit_gadget_changes_saved_sucessfully'],
                messages['create_edit_gadget_edit_gadget'],
                4000
            );
        }
    }, [updateResponse])

    useEffect(() => {
        if (errorUserGadget?.error) {
            NotificationManager.error(
                "Error",
                messages["error"],
                4000
            );
        }
    }, [errorUserGadget])

    useEffect(() => {
        if (gadGet.hasOwnProperty('name')) {
            setSelectGadget(gadGet);
        }
    }, [openCreateEdit, gadGet])

    const title = (
        (
            gadGet.hasOwnProperty('name')) ?
            `${messages['create_edit_gadget_edit_gadget']} '${gadGet.name}'`
            :
            messages['create_edit_gadget_create_gadget'] + (selectGadget.hasOwnProperty('name') ? ` '${selectGadget.name}'` : "")
    );

    return (
        <div className='create-edit-modal'>
            <Modal
                open={openCreateEdit}
                size={size_modal}
                title={title}
                handleClose={() => handleClose()}
                position="right"
                loading={loadingCreateEdit}
            >
                <ModalBody className="modal-create-edit-dashboard">
                    <div className="modal-create-edit-container" style={{ marginBottom: !selectGadget.hasOwnProperty('name') && 100 }}>
                        {
                            (!selectGadget.hasOwnProperty('name') && gadGetsType?.length) &&
                            gadGetsType.filter((x => x.visible == true)).map((gadGetItem, key) => {
                                return (
                                    <div key={key} className="item-gadget-create">
                                        <div className="item-gadget-imagen-container">
                                            <img className="modal-create-edit-image" src={`${urlImagePreview}${gadGetItem.type}.png`} />
                                        </div>
                                        <div style={{ textAlign: 'left', flexBasis: 0, flexGrow: "1" }} >
                                            <Typography variant="body2"
                                                style={{ fontWeight: 700, marginBottom: 5, fontSize: "1.2rem" }}
                                            >
                                                {gadGetItem.name}
                                            </Typography>
                                            <Typography
                                                variant="subtitle2"
                                                style={{ marginBottom: 10, fontSize: "0.9rem" }}
                                            >
                                                {gadGetItem.description}
                                            </Typography>
                                            <Button
                                                onClick={() => {
                                                    setSelectGadget(gadGetItem);
                                                }}
                                                className="btn-dark-blue"
                                                style={{ paddingLeft: "1.5rem", paddingRight: "1.5rem" }}
                                            >
                                                {messages['select']}
                                            </Button>
                                        </div>
                                    </div>
                                )
                            })
                        }
                        {(selectGadget.hasOwnProperty('name') &&
                            <CreateEditView selectGadget={selectGadget} onSubmit={onSubmit} handleClose={returnMain} />
                        )}
                    </div>
                </ModalBody>
            </Modal>
        </div>
    )
}

export default CreateEditGadget